"use client";
import React from "react";

import clsx from 'clsx';

const AuroraBackground = ({
  children,
  className,
  showRadialGradient = true,
  showGrid = true,
  showGlow = true,
  animated = true,
  ...props
}) => {
  // Sin `overflow-hidden` en este div a propósito: un ancestro con overflow
  // oculto rompe `position: sticky`, y la navbar de la landing es sticky. El
  // recorte de los blobs decorativos lo hace igual el contenedor de más abajo,
  // que ya tiene su propio overflow-hidden, así que acá era redundante.
  return (
    <div
      className={clsx(
        "relative min-h-screen w-full bg-white antialiased",
        className
      )}
      {...props}
    >
      {/* Aurora Effect with Enhanced Animation */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Primary Aurora Elements */}
        <div className="absolute -inset-[20px] opacity-80">
          {/* Large Purple Aurora */}
          <div 
            className={clsx(
              "absolute top-0 -left-8 h-96 w-96 rounded-full bg-purple-500 opacity-80 blur-[120px] filter",
              animated && "animate-pulse"
            )}
            style={{
              animation: animated ? "aurora-float-1 8s ease-in-out infinite" : "none"
            }}
          ></div>
          
          {/* Large Blue Aurora */}
          <div 
            className={clsx(
              "absolute top-0 -right-8 h-96 w-96 rounded-full bg-blue-500 opacity-80 blur-[120px] filter",
              animated && "animate-pulse"
            )}
            style={{
              animation: animated ? "aurora-float-2 10s ease-in-out infinite reverse" : "none"
            }}
          ></div>
          
          {/* Large Pink Aurora */}
          <div 
            className={clsx(
              "absolute -bottom-16 left-20 h-96 w-96 rounded-full bg-pink-500 opacity-80 blur-[120px] filter",
              animated && "animate-pulse"
            )}
            style={{
              animation: animated ? "aurora-float-3 12s ease-in-out infinite" : "none"
            }}
          ></div>
          
          {/* Large Green Aurora */}
          <div 
            className={clsx(
              "absolute top-1/2 right-1/4 h-[500px] w-[500px] rounded-full bg-green-500 opacity-70 blur-[150px] filter",
              animated && "animate-pulse"
            )}
            style={{
              animation: animated ? "aurora-float-4 15s ease-in-out infinite reverse" : "none"
            }}
          ></div>
        </div>

        {/* Secondary Aurora Elements */}
        <div className="absolute inset-0">
          {/* Yellow Aurora */}
          <div 
            className="absolute top-1/4 left-1/4 h-80 w-80 rounded-full bg-yellow-400 opacity-60 blur-[100px] animate-pulse"
            style={{ 
              animation: animated ? "aurora-float-5 9s ease-in-out infinite" : "none",
              animationDelay: "1s"
            }}
          ></div>
          
          {/* Orange Aurora */}
          <div 
            className="absolute bottom-1/4 right-1/4 h-64 w-64 rounded-full bg-orange-400 opacity-70 blur-[80px] animate-pulse"
            style={{ 
              animation: animated ? "aurora-float-6 11s ease-in-out infinite reverse" : "none",
              animationDelay: "2s"
            }}
          ></div>
          
          {/* Cyan Aurora */}
          <div 
            className="absolute top-1/3 right-1/3 h-72 w-72 rounded-full bg-cyan-400 opacity-65 blur-[90px] animate-pulse"
            style={{ 
              animation: animated ? "aurora-float-7 13s ease-in-out infinite" : "none",
              animationDelay: "3s"
            }}
          ></div>
          
          {/* Magenta Aurora */}
          <div 
            className="absolute bottom-1/3 left-1/3 h-56 w-56 rounded-full bg-magenta-400 opacity-75 blur-[70px] animate-pulse"
            style={{ 
              animation: animated ? "aurora-float-8 7s ease-in-out infinite reverse" : "none",
              animationDelay: "4s"
            }}
          ></div>
        </div>

        {/* Additional Floating Elements */}
        <div className="absolute inset-0">
          <div 
            className="absolute top-1/6 left-1/6 h-48 w-48 rounded-full bg-indigo-400 opacity-50 blur-[60px] animate-pulse"
            style={{ 
              animation: animated ? "aurora-float-9 14s ease-in-out infinite" : "none",
              animationDelay: "5s"
            }}
          ></div>
          
          <div 
            className="absolute bottom-1/6 right-1/6 h-40 w-40 rounded-full bg-teal-400 opacity-60 blur-[50px] animate-pulse"
            style={{ 
              animation: animated ? "aurora-float-10 16s ease-in-out infinite reverse" : "none",
              animationDelay: "6s"
            }}
          ></div>
        </div>
      </div>

      {/* Radial Gradient */}
      {showRadialGradient && (
        <div className="absolute inset-0 bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_30%,black)]"></div>
      )}

      {/* Grid Pattern */}
      {showGrid && (
        <div className="absolute inset-0 bg-white [mask-image:repeating-linear-gradient(90deg,transparent,transparent_50px,black_50px,black_51px),repeating-linear-gradient(180deg,transparent,transparent_50px,black_50px,black_51px)]"></div>
      )}

      {/* Enhanced Glow Effect */}
      {showGlow && (
        <div className="absolute inset-0 bg-gradient-to-r from-purple-200/40 via-transparent to-blue-200/40 opacity-60"></div>
      )}

      {/* Content */}
      <div className="relative z-10">{children}</div>

      {/* Enhanced CSS Animations */}
      <style>{`
        @keyframes aurora-float-1 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.8;
          }
          25% {
            transform: translateY(-30px) rotate(90deg) scale(1.2);
            opacity: 1;
          }
          50% {
            transform: translateY(-60px) rotate(180deg) scale(1.4);
            opacity: 0.9;
          }
          75% {
            transform: translateY(-30px) rotate(270deg) scale(1.2);
            opacity: 1;
          }
        }
        
        @keyframes aurora-float-2 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.8;
          }
          25% {
            transform: translateY(-40px) rotate(-90deg) scale(1.3);
            opacity: 1;
          }
          50% {
            transform: translateY(-80px) rotate(-180deg) scale(1.6);
            opacity: 0.9;
          }
          75% {
            transform: translateY(-40px) rotate(-270deg) scale(1.3);
            opacity: 1;
          }
        }
        
        @keyframes aurora-float-3 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.8;
          }
          33% {
            transform: translateY(-25px) rotate(120deg) scale(1.1);
            opacity: 1;
          }
          66% {
            transform: translateY(-50px) rotate(240deg) scale(1.3);
            opacity: 0.9;
          }
        }
        
        @keyframes aurora-float-4 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.7;
          }
          20% {
            transform: translateY(-20px) rotate(72deg) scale(1.1);
            opacity: 0.9;
          }
          40% {
            transform: translateY(-40px) rotate(144deg) scale(1.2);
            opacity: 0.8;
          }
          60% {
            transform: translateY(-60px) rotate(216deg) scale(1.3);
            opacity: 0.9;
          }
          80% {
            transform: translateY(-40px) rotate(288deg) scale(1.2);
            opacity: 0.8;
          }
        }
        
        @keyframes aurora-float-5 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-35px) rotate(180deg) scale(1.25);
            opacity: 0.8;
          }
        }
        
        @keyframes aurora-float-6 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-45px) rotate(-180deg) scale(1.35);
            opacity: 0.9;
          }
        }
        
        @keyframes aurora-float-7 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.65;
          }
          33% {
            transform: translateY(-30px) rotate(120deg) scale(1.15);
            opacity: 0.85;
          }
          66% {
            transform: translateY(-60px) rotate(240deg) scale(1.3);
            opacity: 0.75;
          }
        }
        
        @keyframes aurora-float-8 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.75;
          }
          25% {
            transform: translateY(-25px) rotate(90deg) scale(1.1);
            opacity: 0.95;
          }
          50% {
            transform: translateY(-50px) rotate(180deg) scale(1.2);
            opacity: 0.85;
          }
          75% {
            transform: translateY(-25px) rotate(270deg) scale(1.1);
            opacity: 0.95;
          }
        }
        
        @keyframes aurora-float-9 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.5;
          }
          50% {
            transform: translateY(-40px) rotate(180deg) scale(1.3);
            opacity: 0.7;
          }
        }
        
        @keyframes aurora-float-10 {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-35px) rotate(-180deg) scale(1.25);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export { AuroraBackground }; 